import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { PHOTOGRAPHY_PROJECTS, PHOTOGRAPHY_SERVICE_AREA, CREATIVE_LEAD, photographyProject } from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.

const INLINE_LINK =
  'text-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-4'

const PROBLEMS = [
  {
    problem: 'Your photos undersell the performance',
    description:
      'A phone shot from the back row is what ends up on the website, the season brochure, and the grant application. The audience that night heard something better than that photo shows.',
  },
  {
    problem: 'Press and grants keep asking for images you do not have',
    description:
      'High-resolution, publishable photos are the first thing a newspaper, a presenter, or a funder requests, and the folder is empty or five years old.',
  },
  {
    problem: 'Your people pages are a patchwork',
    description:
      'One director has a studio headshot, another a cropped wedding photo, a third a selfie. Shot together, your roster reads like one organization.',
  },
  {
    problem: 'The season launches with last season’s images',
    description:
      'New programming, new venue, new faces, and the announcement still runs on photos from two years ago because nobody was hired to take new ones.',
  },
]

// Real work: the 2026 Chroma International Music Festival (Miskolc), shot by
// Danielle Dinstman. Reuses the optimized assets committed for the /portfolio
// Photography gallery item (public/portfolio/photography) rather than a second
// copy. A curated one-per-program set; the full gallery lives on /portfolio.
const CHROMA_WORK = [
  {
    src: '/portfolio/photography/DSCF2911-thumb.webp',
    caption: 'Faculty',
    alt: 'The Chroma Festival faculty in the organ hall, Miskolc',
  },
  {
    src: '/portfolio/photography/DSCF2907-thumb.webp',
    caption: 'Piano, young artists',
    alt: 'The young-artist piano cohort at the Chroma Festival, Miskolc',
  },
  {
    src: '/portfolio/photography/DSCF2898-thumb.webp',
    caption: 'Piano, adult program',
    alt: 'The adult piano program at the Chroma Festival, Miskolc',
  },
  {
    src: '/portfolio/photography/DSCF2929-thumb.webp',
    caption: 'Composition',
    alt: 'The composition program at the Chroma Festival, Miskolc',
  },
]

const FAQS = [
  {
    q: 'Where do you shoot?',
    a: PHOTOGRAPHY_SERVICE_AREA,
  },
  {
    q: 'What do we get after a session?',
    a: 'An edited, web-ready gallery plus full-resolution files. Delivered sized for your website, socials, and print, so nobody on your team has to resize anything.',
  },
  {
    q: 'Can you shoot a live concert without disrupting it?',
    a: 'Yes. Performance coverage is planned around the program: where to stand, when to move, what stays silent. You get the concert documented and your audience never notices the camera.',
  },
  {
    q: 'Who owns the photos?',
    a: 'You do, with full usage rights for your marketing, press, and grant materials. Same policy as everything else here: you can leave with everything.',
  },
]

export default function PhotographyPage() {
  const breadcrumbs = generateBreadcrumbs('/services/photography', breadcrumbLabels)

  useSEO({
    title: 'Photography for Music Organizations',
    description: `Concert photography, headshots, and season photography days for music organizations in the Los Angeles area. Edited, web-ready galleries; headshot sessions ${photographyProject('photo-headshots').price}.`,
    url: '/services/photography',
    canonical: '/services/photography',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Photography for Music Organizations',
      description:
        'Concert and rehearsal photography, roster headshots, and season photography days for festivals, churches, choruses, and music schools.',
      url: '/services/photography',
    }),
  })

  // The hero keeps its entrance; everything below the fold is static. The
  // per-section staggered reveals and card hover lifts were the stock kit.
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  return (
    <div className="mx-auto max-w-6xl space-y-28 px-5 py-16 md:space-y-36 md:px-12">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Photos that match the performance</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          The concert lasts one night. The photos are what your website, your press kit, and your next grant application
          run on for years. Shot by a working artist who knows where to stand in a hall.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Performance coverage, headshots, and season photography days, delivered as edited, web-ready galleries you own
          outright.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
          <Link to="/services/design">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              Graphic Design
            </Button>
          </Link>
        </div>
      </section>

      {/* Problems: a typographic list, name left and explanation right, in
          place of four bordered cards. Space alone separates the rows. */}
      <section aria-labelledby="problems" className="space-y-12">
        <h2 id="problems" className="text-fluid-h3 max-w-3xl">
          Why organizations book a photographer
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {PROBLEMS.map((item) => (
            <li key={item.problem} className="grid gap-3 md:grid-cols-12 md:items-baseline md:gap-8">
              <h3 className="text-fluid-h4 md:col-span-5">{item.problem}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{item.description}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Who does the work */}
      <section aria-labelledby="who-does-the-work" className="grid gap-6 md:grid-cols-12 md:gap-8">
        <h2 id="who-does-the-work" className="text-fluid-h3 md:col-span-5">
          An artist behind the camera
        </h2>
        <div className="text-muted-foreground space-y-4 leading-relaxed md:col-span-7">
          <p className="text-fluid-body max-w-[60ch]">{CREATIVE_LEAD.credit}</p>
          <p className="max-w-[60ch]">
            Someone who has hung a photography exhibition reads a concert differently than a generalist with a camera:
            light, gesture, and the moment a phrase lands.
          </p>
        </div>
      </section>

      {/* Recent work: the Chroma Festival. The photographs are the proof, so
          they run frameless with a plain caption beneath, not inside bordered
          tiles. */}
      <section aria-labelledby="recent-work" className="space-y-12">
        <div className="grid gap-6 md:grid-cols-12 md:gap-8">
          <h2 id="recent-work" className="text-fluid-h3 md:col-span-5">
            Recent work: the Chroma Festival
          </h2>
          <p className="text-muted-foreground text-fluid-body max-w-[60ch] leading-relaxed md:col-span-7">
            The 2026 Chroma International Music Festival in Miskolc, Hungary: the full company, the faculty, and each
            program, from the young artists to the composers.
          </p>
        </div>
        <figure className="space-y-3">
          <div className="bg-surface overflow-hidden rounded-md">
            <img
              src="/portfolio/photography/DSCF2893.webp"
              alt="The full company of the 2026 Chroma International Music Festival gathered in the organ hall, Miskolc"
              width={1600}
              height={1066}
              loading="lazy"
              className="aspect-[3/2] w-full object-cover"
            />
          </div>
          <figcaption className="text-muted-foreground text-sm">The full company, Miskolc</figcaption>
        </figure>
        <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          {CHROMA_WORK.map((item) => (
            <figure key={item.src} className="space-y-3">
              <div className="bg-surface overflow-hidden rounded-md">
                <img
                  src={item.src}
                  alt={item.alt}
                  width={760}
                  height={507}
                  loading="lazy"
                  className="aspect-[3/2] w-full object-cover"
                />
              </div>
              <figcaption className="text-muted-foreground text-sm">{item.caption}</figcaption>
            </figure>
          ))}
        </div>
        <p className="text-muted-foreground max-w-[60ch] text-sm leading-relaxed">
          Photographs by Danielle Dinstman, on location at the Chroma International Music Festival.{' '}
          <Link to="/portfolio/chroma-festival-photography" className={INLINE_LINK}>
            See the full set
          </Link>
          .
        </p>
      </section>

      {/* Sessions: a priced catalog of distinct sessions, set as a typographic
          list rather than a card grid. */}
      <section aria-labelledby="sessions" className="space-y-12">
        <h2 id="sessions" className="text-fluid-h3">
          The sessions
        </h2>
        <ul className="grid gap-x-10 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
          {PHOTOGRAPHY_PROJECTS.map((project) => (
            <li key={project.id} className="space-y-3">
              <h3 className="text-fluid-h4">{project.name}</h3>
              <p className="font-medium">{project.price}</p>
              <p className="text-muted-foreground max-w-[48ch] leading-relaxed">{project.note}</p>
            </li>
          ))}
        </ul>
        <p className="text-muted-foreground max-w-[60ch] leading-relaxed">
          {PHOTOGRAPHY_SERVICE_AREA} Need the graphics side too? See{' '}
          <Link to="/services/design" className={INLINE_LINK}>
            Graphic Design
          </Link>
          .
        </p>
      </section>

      {/* FAQ */}
      <section aria-labelledby="questions" className="space-y-12">
        <h2 id="questions" className="text-fluid-h3">
          Questions
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {FAQS.map((f) => (
            <li key={f.q} className="grid gap-3 md:grid-cols-12 md:gap-8">
              <h3 className="text-xl leading-snug md:col-span-5">{f.q}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{f.a}</p>
            </li>
          ))}
        </ul>
        <div className="space-y-3">
          <a href="/book" target="_blank" rel="noopener noreferrer" className="inline-block">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
          <p className="text-muted-foreground text-sm">
            Full-resolution files and full usage rights, yours after every session.
          </p>
        </div>
      </section>
    </div>
  )
}
